const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory } = require('react-router-dom')
const { Row, Col, Button, UncontrolledTooltip } = require('reactstrap')
const { useFormatBytes, useFormatTimestamp, useHexToString, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner } = require('@components')

require('@styles/pages/block/details.block.css')

const SlotDetails = props => {
    const { loading, data, latestBlock } = props
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestamp()
    const formatHexToString = useHexToString()
    const isMobile = useResponsive(BREAKPOINTS.small)

    const [slotDetails, setSlotDetails] = useState([])

    useEffect(() => {
        if (!data || !latestBlock) return

        const fields = {
            divider: {
                id: 'divider'
            },
            slot: {
                id: 'slot',
                label: 'Slot Number',
                value: (
                    <div className='block-navigation'>
                        {data.slot}
                        <div className='block-nav-buttons'>
                            <div className='block-nav-button-wrapper' id='prev-block'>
                                <Button
                                    className='block-nav-button'
                                    disabled={data.slot === 0}
                                    onClick={() => {
                                        const prevSlot = parseInt(data.slot) - 1
                                        if (prevSlot >= 0) {
                                            history.push(`/${network.name}/slot/${prevSlot}`)
                                        }
                                    }}
                                >
                                    <i className='fas fa-chevron-left' />
                                </Button>
                                <UncontrolledTooltip placement='top' target='prev-block'>
                                    {data.slot === 0 ? 'You have reached the earliest slot' : 'View previous slot'}
                                </UncontrolledTooltip>
                            </div>
                            <div className='block-nav-button-wrapper' id='next-block'>
                                <Button
                                    className='block-nav-button'
                                    disabled={parseInt(data.slot) >= latestBlock}
                                    onClick={() => {
                                        const nextSlot = parseInt(data.slot) + 1
                                        history.push(`/${network.name}/slot/${nextSlot}`)
                                    }}
                                >
                                    <i className='fas fa-chevron-right' />
                                </Button>
                                <UncontrolledTooltip placement='top' target='next-block'>
                                    {parseInt(data.slot) >= latestBlock
                                        ? 'You have reached the latest slot'
                                        : 'View next slot'}
                                </UncontrolledTooltip>
                            </div>
                        </div>
                    </div>
                ),
                tooltip: 'Beacon chain slot that this block represents.'
            },
            epoch: {
                id: 'epoch',
                label: 'Epoch Number',
                value: (
                    <span
                        className={`${network.name}-text-color cursor-pointer`}
                        onClick={() => history.push(`/${network.name}/epoch/${data.epoch}`)}
                    >
                        {data.epoch}
                    </span>
                ),
                tooltip: 'Epoch of the current block.'
            },
            status: {
                id: 'status',
                label: 'Status',
                value: <span style={{ color: 'var(--success-color)' }}>proposed</span>,
                tooltip: 'Status of the current slot'
            },
            timestamp: {
                id: 'timestamp',
                label: 'Age',
                value: (
                    <>
                        <i className='fas fa-clock' style={{ fontSize: '12px' }} />
                        &nbsp;
                        {formatTimestamp(data.timestamp)}
                    </>
                ),
                tooltip: 'Date and time of the current slot.'
            },
            proposer: {
                id: 'proposer',
                label: 'Proposer',
                value: (
                    <>
                        <i
                            className='fas fa-user'
                            style={{
                                fontSize: '14px',
                                color: 'var(--grey-color)',
                                marginRight: '10px'
                            }}
                        />
                        <span
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => history.push(`/${network.name}/validator/${data.proposerIndex}`)}
                        >
                            {data.proposerIndex}
                        </span>
                        &nbsp; (
                        <span
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => history.push(`/${network.name}/validator/${data.proposerIndex}`)}
                        >
                            {formatBytes(data.proposerPubkey, 64, 10, 10)}
                        </span>
                        )
                    </>
                ),
                tooltip: "The proposer of the current slot (Validator's public key)."
            },
            blockRootHash: {
                id: 'block-root-hash',
                label: 'Block Root Hash',
                value: data.blockRoot,
                tooltip: '32 byte root of the current slot.'
            },
            parentRootHash: {
                id: 'parent-root-hash',
                label: 'Parent Root Hash',
                value: (
                    <span
                        className={`${network.name}-text-color cursor-pointer`}
                        onClick={() => history.push(`/${network.name}/slot/${data.slot - 1}`)}
                    >
                        {data.parentRoot}
                    </span>
                ),
                tooltip: '32 byte root of the parrent slot.'
            },
            stateRoot: {
                id: 'state-root',
                label: 'State Root',
                value: data.stateRoot,
                tooltip: '32 byte root of the resulting state after processing this slot.'
            },
            randaoReveal: {
                id: 'randao-reveal',
                label: 'Randao Reveal',
                value: data.randaoReveal,
                tooltip: 'The validators RANDAO reveal 96 byte value.'
            },
            graffiti: {
                id: 'graffiti',
                label: 'Graffiti',
                value: (
                    <>
                        <div>{formatHexToString(data.graffiti)}</div>
                        (Hex: <span>{data.graffiti}</span>)
                    </>
                ),
                tooltip:
                    '32 byte field of arbitrary data. This field may contain any data and is not used for anything other than a fun message.'
            },
            eth1BlockHash: {
                id: 'eth1-block-hash',
                label: 'Eth_1 Block Hash',
                value: (
                    <span
                        className={`${network.name}-text-color cursor-pointer`}
                        onClick={() => history.push(`/${network.name}/block/${data.slot - 1}`)}
                    >
                        {data.eth1BlockHash}
                    </span>
                ),
                tooltip: 'The 32 byte block hash of the Ethereum 1.x block considered for deposit inclusion.'
            },
            eth1DepositRoot: {
                id: 'eth1-deposit-root',
                label: 'Eth_1 Deposit Root',
                value: data.eth1DepositRoot,
                tooltip: 'The 32 byte deposit tree root for the last deposit included in this block.'
            },
            eth1DepositCount: {
                id: 'eth1-deposit-count',
                label: 'Eth_1 Deposit Count',
                value: data.eth1DepositCount,
                tooltip:
                    'The total number of deposits included in the beacon chain since genesis including the deposits in this block.'
            },
            signature: {
                id: 'signature',
                label: 'Signature',
                value: data.signature,
                tooltip: '96 byte BLS signature from the validator that produced this block.'
            },
            slashing: {
                id: 'slashing',
                label: 'Slashing P/A',
                value: (
                    <>
                        {data.proposerSlashingsCount} proposer slashed / {data.attesterSlashingsCount} attester slashed
                    </>
                ),
                tooltip:
                    'Proposer slashings are proofs that a slashable offense has been committed by proposing two conflicting blocks from the same validator / Attestor slashings are proofs that a slashable offense has been committed by attesting to two conflicting pieces of information by the same validator.'
            }
        }

        const slotDetailsOrder = [
            'slot',
            'epoch',
            'status',
            'timestamp',
            'proposer',
            'divider',
            'blockRootHash',
            'parentRootHash',
            'stateRoot',
            'divider',
            'randaoReveal',
            'divider',
            'graffiti',
            'divider',
            'eth1BlockHash',
            'eth1DepositRoot',
            'eth1DepositCount',
            'divider',
            'signature',
            'divider',
            'slashing'
        ]
        setSlotDetails(slotDetailsOrder.map(key => fields[key]))
    }, [data, latestBlock])

    return (
        <>
            {loading ? (
                <div className='box table-section'>
                    <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            ) : (
                <>
                    <div className='box table-section'>
                        {slotDetails.map(item => (
                            <Row className='table-info-row' noGutters>
                                {item.id === 'divider' ? (
                                    <div className='table-divider' />
                                ) : (
                                    <>
                                        <Col xs={12} sm={4} className='d-flex align-items-start'>
                                            <span className='info-icon-wrapper' id={item.id}>
                                                <i className='fas fa-question-circle info-icon' />
                                            </span>
                                            <span className='info-label'>{item.label}:</span>
                                        </Col>
                                        <UncontrolledTooltip placement='top' target={item.id}>
                                            {item.tooltip}
                                        </UncontrolledTooltip>
                                        <Col
                                            xs={12}
                                            sm={8}
                                            className={`d-flex align-items-center ${isMobile && 'mt-2'}`}
                                            style={{ flexWrap: 'wrap', wordBreak: 'break-all' }}
                                        >
                                            <span className='info-value' style={{ width: '100%' }}>
                                                {item.value}
                                            </span>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        ))}
                    </div>
                </>
            )}
        </>
    )
}

module.exports = { SlotDetails }
